import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { makeStyles, createStyles } from "@mui/styles";
import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  debounce,
  Grid,
  Paper,
  Stack,
  SxProps,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import React, {
  useEffect,
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import theme from "../../../components/theme/theme";
import CommonSection from "./common-section";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../common/store/store";
import { getUserInputForStepId } from "../../../components/wizard/util";
import { Parameter, ParameterValue } from "../../../common/types/parameters";
import {
  calculateOutput,
  ModuleCalculationResult,
  scheduleUpcomingCalculation,
  isCalculationAborted,
} from "../../../api/module-api";
import BuildingElectricity from "./building-electricity-section";
import DistrictHeating from "./district-heating-section";
import Pellet from "./pellet-section";
import GroundSourceHeatPump from "./ground-source-section";
import AirSourceHeatPump from "./air-source-section";
import ExhaustAirHeatPump from "./exhaust-air-section";
import DetailsChart from "./graph";
import {
  getParameterStates,
  setUserInput as setModuleUserInput,
  resetUserInputs as resetModuleUserInput,
  deviatesFromDefaults as moduleInputDeviatesFromDefaults,
} from "../../../common/store/module-parameters-slice";
import {
  getDisplayValue,
  getType,
  parseAsSelectionAlternative,
} from "../../../components/data-presentation/util";
import "../module.css";
import useAPIError from "../../../common/hooks/useAPIError";
import { isCalculated } from "../../../common/util/heating-technology-visibility";
import { getLabel } from "../../../api/label-api";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@mui/icons-material/Print";
import { ModuleInfo } from "../../../common/types/modules";
import useWizard from "../../../common/hooks/useWizard";
import HideableTab from "../../../components/navigation/hideable-tab";
import useHeatingTechnologySelection from "../../../common/hooks/useHeatingTechnologySelection";
import HideableChip from "../../../components/navigation/hideable-chip";

const useStyles = makeStyles(() =>
  createStyles({
    card: {
      padding: "15px",
    },
  })
);

const moduleId = "detaljer";

const paperElevation = 3;

export default function DetaljeradeResultat(params: { sx?: SxProps }) {
  let { customer } = useParams();
  let { economicProfile } = useParams();
  const { addError, deleteError } = useAPIError();
  const [isCalculating, setIsCalculating] = useState(false);
  useEffect(() => {
    setIsCalculating(false);
  }, [customer, economicProfile]);
  const {
    steps: wizardSteps,
    updateParameterValue: updateWizardParameterValue,
    isWizardParameter,
    wizardIsCompleted,
    userInputDeviatesFromDefaults: wizardUserInputDeviatesFromDefaults,
    resetToDefaults: resetWizardUserInput,
  } = useWizard(customer!, economicProfile!);

  //printing
  const [toPrint, setToPrint] = useState<boolean>(false);
  const [labelTitle, setLabelTitle] = useState<string>("");
  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const moduleInfo: Omit<ModuleInfo, "accessibleToUser"> = {
    id: moduleId,
    displayName: "Detaljerad kostnadskalkyl",
  };

  let moduleParameterInputs = useAppSelector((state) =>
    getParameterStates(state, {
      customer: customer,
      economicProfile: economicProfile,
    })
  );

  const [outputParameters, setOutputParameters] = useState<Parameter[]>([]);
  const includeVat: boolean = JSON.parse(
    outputParameters.find((parameter) => parameter.id === "VATSelection")
      ?.value ?? "false"
  );

  let deviatesFromDefaults = useAppSelector(
    (state) =>
      wizardUserInputDeviatesFromDefaults() ||
      moduleInputDeviatesFromDefaults(state, {
        customer: customer,
        economicProfile: economicProfile,
      })
  );

  const [districtHeatingLabels, setDistrictHeatingLabels] =
    useState<Parameter>();
  const [groundSourceHeatPumpLabels, setGroundSourceHeatPumpLabels] =
    useState<Parameter>();
  const [airSourceHeatPumpLabels, setAirSourceHeatPumpLabels] =
    useState<Parameter>();
  const [exhaustAirHeatPumpLabels, setExhaustAirHeatPumpLabels] =
    useState<Parameter>();
  const [pelletLabels, setPelletLabels] = useState<Parameter>();
  const [buildingLabels, setBuildingLabels] = useState<Parameter>();

  const { isHidden, switchVisibilitySetting } = useHeatingTechnologySelection();

  const heatingTechnologies = (toPrint: boolean) =>
    [
      {
        id: "DistrictHeating",
        title: districtHeatingLabels?.displayName || "Fjärrvärme",
        component: (
          <DistrictHeating
            description={districtHeatingLabels?.description ?? undefined}
            parameters={outputParameters}
            includeVat={includeVat}
            onValidParameterChange={onValidParameterChange}
            printTitle={districtHeatingLabels?.displayName || "Fjärrvärme"}
            toPrint={toPrint}
          />
        ),
        isHeatingTechnology: true,
      },
      {
        id: "GroundSource",
        title: groundSourceHeatPumpLabels?.displayName || "Bergvärme",
        component: (
          <GroundSourceHeatPump
            description={groundSourceHeatPumpLabels?.description ?? undefined}
            parameters={outputParameters}
            includeVat={includeVat}
            onValidParameterChange={onValidParameterChange}
            onSectionChange={setActiveTab}
            printTitle={groundSourceHeatPumpLabels?.displayName || "Bergvärme"}
            toPrint={toPrint}
          />
        ),
        isHeatingTechnology: true,
      },
      {
        id: "AirSource",
        title: airSourceHeatPumpLabels?.displayName || "Luft/vatten-värmepump",
        component: (
          <AirSourceHeatPump
            toPrint={toPrint}
            description={airSourceHeatPumpLabels?.description ?? undefined}
            parameters={outputParameters}
            includeVat={includeVat}
            printTitle={
              airSourceHeatPumpLabels?.displayName || "Luft/vatten-värmepump"
            }
            onValidParameterChange={onValidParameterChange}
            onSectionChange={setActiveTab}
          />
        ),
        isHeatingTechnology: true,
      },
      {
        id: "ExhaustAir",
        title: exhaustAirHeatPumpLabels?.displayName || "Frånluftsvärmepump",
        component: (
          <ExhaustAirHeatPump
            toPrint={toPrint}
            description={exhaustAirHeatPumpLabels?.description ?? undefined}
            parameters={outputParameters}
            includeVat={includeVat}
            printTitle={
              exhaustAirHeatPumpLabels?.displayName || "Frånluftsvärmepump"
            }
            onValidParameterChange={onValidParameterChange}
            onSectionChange={setActiveTab}
          />
        ),
        isHeatingTechnology: true,
      },
      {
        id: "Pellet",
        title: pelletLabels?.displayName || "Pellets",
        component: (
          <Pellet
            toPrint={toPrint}
            description={pelletLabels?.description ?? undefined}
            parameters={outputParameters}
            includeVat={includeVat}
            printTitle={pelletLabels?.displayName || "Pellets"}
            onValidParameterChange={onValidParameterChange}
          />
        ),
        isHeatingTechnology: true,
      },
    ].filter((tech) => isCalculated(tech, outputParameters));

  const sections = (toPrint: boolean) => [
    {
      id: "Common",
      title: "Övergripande",
      component: (
        <CommonSection
          toPrint={toPrint}
          parameters={outputParameters}
          includeVat={includeVat}
          printTitle={"Övergripande"}
          onValidParameterChange={onValidParameterChange}
        />
      ),
      isHeatingTechnology: false,
    },
    {
      id: "Electricity",
      title: buildingLabels?.displayName || "Fastighetsel",
      component: (
        <BuildingElectricity
          toPrint={toPrint}
          description={buildingLabels?.description ?? undefined}
          parameters={outputParameters}
          includeVat={includeVat}
          printTitle={buildingLabels?.displayName || "Fastighetsel"}
          onValidParameterChange={onValidParameterChange}
        />
      ),
      isHeatingTechnology: false,
    },
    ...heatingTechnologies(toPrint),
  ];

  const [activeTab, setActiveTab] = useState("Common");
  const handleTabSelection = (
    event: React.SyntheticEvent,
    selectedTab: string
  ) => {
    setActiveTab(selectedTab);
  };

  const dispatch = useAppDispatch();

  function onValidParameterChange(id: string, value: string) {
    if (isWizardParameter(id)) {
      let parameter = outputParameters.find((parameter) => parameter.id === id);

      if (parameter !== null && parameter !== undefined) {
        saveWizardParameter(
          id,
          value,
          getDisplayValue(
            value,
            includeVat,
            getType(parameter),
            parameter?.stylingConfig,
            true
          ) ?? value
        );
      }
    } else {
      saveModuleParameter(id, value);
    }
  }

  function saveWizardParameter(
    id: string,
    value: string,
    formattedValue: string
  ) {
    updateWizardParameterValue({ id, value }, formattedValue);
  }

  function saveModuleParameter(id: string, value: string) {
    dispatch(
      setModuleUserInput({
        id: id,
        value: value,
        profile: { customer: customer, economicProfile: economicProfile },
      })
    );
  }

  const calculateOutputDelayed = useCallback(
    debounce(
      (
        inputs: ParameterValue[],
        calculationAbortController: AbortController,
        callback: (output: Parameter[]) => void
      ) => {
        setIsCalculating(true);
        calculateOutput(moduleId, inputs, customer, economicProfile)
          .then((calculationResult) => {
            if (
              !wasCalculationAborted(
                calculationResult,
                calculationAbortController
              )
            ) {
              callback(calculationResult as Parameter[]);
            }
          })
          .catch(() => addError("Error"))
          .finally(() => {
            if (!isCalculationAborted(calculationAbortController)) {
              setIsCalculating(false);
            }
          });
      },
      600
    ),
    [customer, economicProfile, addError]
  );

  useEffect(() => {
    if (wizardIsCompleted) {
      const calculationAbortController = scheduleUpcomingCalculation();
      let inputs = wizardSteps
        .flatMap((step) => step.inputParameters)
        .map(
          (parameter) =>
            ({ id: parameter.id, value: parameter.value }) as ParameterValue
        )
        .concat(moduleParameterInputs);
      calculateOutputDelayed(
        inputs,
        calculationAbortController,
        (parameters) => {
          deleteError();
          setOutputParameters(parameters);
        }
      );
      return () => {
        calculateOutputDelayed.clear();
        calculationAbortController.abort();
      };
    }
  }, [
    moduleParameterInputs,
    customer,
    economicProfile,
    wizardIsCompleted,
    wizardSteps,
    calculateOutputDelayed,
    deleteError,
  ]);

  useEffect(() => {
    let districtValue = getUserInputForStepId(
      "DistrictSelected",
      wizardSteps
    )?.at(0)?.value;
    let buildingValue = getUserInputForStepId(
      "BuildingProfile",
      wizardSteps
    )?.at(0)?.value;
    const district = parseAsSelectionAlternative(districtValue)?.value;
    const building = parseAsSelectionAlternative(buildingValue)?.value;
    if (district !== undefined && building !== undefined) {
      getLabel(
        "DistrictHeatingTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setDistrictHeatingLabels(response);
      });
      getLabel(
        "GroundSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setGroundSourceHeatPumpLabels(response);
      });
      getLabel(
        "AirSourceHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setAirSourceHeatPumpLabels(response);
      });
      getLabel(
        "ExhaustAirHeatPumpTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setExhaustAirHeatPumpLabels(response);
      });
      getLabel("PelletTab", customer, district, building, economicProfile).then(
        (response) => {
          setPelletLabels(response);
        }
      );
      getLabel(
        "BuildingElectricityTab",
        customer,
        district,
        building,
        economicProfile
      ).then((response) => {
        setBuildingLabels(response);
      });
    }
  }, [customer, economicProfile, wizardSteps]);

  function resetInput() {
    resetWizardUserInput();
    dispatch(
      resetModuleUserInput({
        profile: { customer: customer, economicProfile: economicProfile },
      })
    );
  }

  const classes = useStyles(theme);

  const [isSmallGraphVisible, setIsSmallGraphVisible] =
    useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const setScroll = () => {
    let bounds = scrollRef?.current?.getBoundingClientRect();

    setIsSmallGraphVisible(
      bounds?.y !== undefined &&
        bounds?.y !== null &&
        bounds?.height > 0 &&
        bounds?.y < -(bounds.height * 0.2) // show small graph when at least 20% of the big one is hidden
    );
  };

  useLayoutEffect(() => {
    window.addEventListener("scroll", setScroll, true);
    return () => {
      window.removeEventListener("scroll", setScroll);
    };
  }, [scrollRef]);

  const onPrint = () => {
    setToPrint(true);
    setTimeout(function () {
      handlePrint();
    }, 1000);
    setTimeout(function () {
      setToPrint(false);
    }, 1100);
  };

  return (
    <Box key={`${moduleId}-module-container`} sx={params.sx}>
      <Paper
        className={classes.card}
        elevation={paperElevation}
        sx={{
          marginBottom: "40px",
          paddingLeft: 4,
          paddingRight: 4,
          paddingBottom: 4,
          textAlign: "center",
        }}
      >
        <TextField
          id="printed-title"
          label="Dokumentbeskrivning"
          sx={{
            width: "400px",
            maxWidth: "100%",
            paddingRight: 4,
          }}
          placeholder="Här kan du namnge din beräkning"
          onChange={(e) => setLabelTitle(e.target.value)}
          value={labelTitle}
          variant="standard"
          type="text"
        />
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => onPrint()}
          sx={{ verticalAlign: "bottom", marginTop: 1 }}
        >
          Skriv ut
        </Button>
      </Paper>

      <Typography variant="h1">{moduleInfo.displayName}</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper className={classes.card} elevation={paperElevation}>
            <Typography paragraph>
              Nedan presenteras ett diagram över kostnaderna för uppvärmning.
              Under detta diagram finns det möjlighet att göra mer detaljerade
              inställningar. Har du till exempel offertuppgifter för en
              värmepump kan dessa användas. Om man ändrar något värde ges en
              möjlighet att återfå urspungsvärdet. Information om olika
              parametrar fås genom att använda informationssymbolen vid aktuell
              parameter.
            </Typography>
          </Paper>
        </Grid>
        <Grid ref={scrollRef} item xs={12}>
          <Typography variant="h2">Total värmekostnad per år</Typography>
          <Paper className={classes.card} elevation={paperElevation}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent={"center"}
              marginBottom={2}
            >
              {heatingTechnologies(false).map((heatingTechnology) => (
                <HideableChip
                  value={heatingTechnology.id}
                  label={heatingTechnology.title}
                  visible={!isHidden(heatingTechnology.id)}
                  onSwitchVisibility={switchVisibilitySetting}
                />
              ))}
            </Stack>
            <DetailsChart
              toPrint={false}
              isCalculating={isCalculating}
              heatingTechnologies={heatingTechnologies(false).map(
                (heatingTechnology) => ({
                  id: heatingTechnology.id,
                  title: heatingTechnology.title,
                  hidden: isHidden(heatingTechnology.id),
                })
              )}
              parameters={outputParameters}
              displayLegends
              includeVat={includeVat}
            />
            <Typography paragraph>
              I diagrammet ovan framgår uppvärmningskostnaden för de olika
              alternativen uppdelat på ett antal olika poster. Du kan släcka och
              tända olika kostnadsposter genom att klicka på förklaringstexterna
              för respektive kostnadspost under grafen.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Collapse in={deviatesFromDefaults}>
            <Alert
              severity="info"
              action={
                <Button color="inherit" size="small" onClick={resetInput}>
                  Återställ till standardvärden
                </Button>
              }
            >
              <AlertTitle>
                Beräkningarna bygger på manuellt justerade värden.
              </AlertTitle>
            </Alert>
          </Collapse>
        </Grid>
        <Grid item xs={12}>
          <Tabs
            value={activeTab}
            onChange={handleTabSelection}
            variant="scrollable"
            scrollButtons={true}
          >
            {sections(false).map((section) =>
              !section.isHeatingTechnology ? (
                <Tab
                  key={`${moduleId}-module-${section.id}`}
                  value={section.id}
                  label={section.title}
                  wrapped
                />
              ) : (
                <HideableTab
                  value={section.id}
                  label={section.title}
                  visible={!isHidden(section.id)}
                  onSwitchVisibility={switchVisibilitySetting}
                />
              )
            )}
          </Tabs>
          {!isHidden(activeTab) ? (
            sections(false).find(
              (heatingTechnology) => activeTab === heatingTechnology.id
            )?.component
          ) : (
            <Paper
              className={classes.card}
              elevation={paperElevation}
              sx={{
                marginBottom: "40px",
                paddingLeft: 4,
                paddingRight: 4,
              }}
            >
              Detta uppvärmningsalternativ är dolt. Aktivera det för att se
              resultat och redigera indata.
            </Paper>
          )}
        </Grid>
      </Grid>
      <div
        className={isSmallGraphVisible ? "smallGraph" : "smallGraphInvisible"}
      >
        <DetailsChart
          toPrint={false}
          isCalculating={isCalculating}
          heatingTechnologies={heatingTechnologies(false).map(
            (heatingTechnology) => ({
              id: heatingTechnology.id,
              title: heatingTechnology.title,
              hidden: isHidden(heatingTechnology.id),
            })
          )}
          parameters={outputParameters}
          includeVat={includeVat}
          displayLegends={false}
        />
      </div>
      {toPrint && (
        <div ref={componentRef} id="toPrint">
          <h1>
            {labelTitle} {new Date().toLocaleString()}
          </h1>
          <Typography variant="h2">Total värmekostnad per år</Typography>
          <DetailsChart
            heatingTechnologies={heatingTechnologies(false).map(
              (heatingTechnology) => ({
                id: heatingTechnology.id,
                title: heatingTechnology.title,
                hidden: isHidden(heatingTechnology.id),
              })
            )}
            parameters={outputParameters}
            displayLegends
            includeVat={includeVat}
            toPrint={true}
          />
          <Typography paragraph>
            I diagrammet ovan framgår uppvärmningskostnaden för de olika
            alternativen uppdelat på ett antal olika poster. Du kan släcka och
            tända olika kostnadsposter genom att klicka på förklaringstexterna
            för respektive kostnadspost under grafen.
          </Typography>
          {sections(true)
            .filter((section) => !isHidden(section.id))
            .map((section) => (
              <div className="section">{section.component}</div>
            ))}
          <div></div>
        </div>
      )}
    </Box>
  );
}

function wasCalculationAborted(
  calculationResult: ModuleCalculationResult,
  calculationAbortController: AbortController
) {
  return (
    isCalculationAborted(calculationAbortController) ||
    calculationResult === undefined
  );
}
